import { ApiError, type ApiErrorBody } from './api-errors';
import {
  clearTokens,
  getStoredAccessToken,
  getStoredRefreshToken,
  saveTokens,
} from './session-tokens';

export { clearTokens, getStoredAccessToken, saveTokens };

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:5311/api/v1';
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID ?? 'admin-web';
const CLIENT_VERSION = import.meta.env.VITE_CLIENT_VERSION ?? '0.1.0';

let onSessionLost: () => void = () => {};

export const configureApi = (options: { onSessionLost?: () => void }): void => {
  if (options.onSessionLost) onSessionLost = options.onSessionLost;
};

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  signal?: AbortSignal;
  /** Sets X-Church-Id for church-scoped platform uploads (e.g. support ticket files). */
  churchId?: string;
}

export const api = {
  get: <T>(path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  postForm: <T>(path: string, body: FormData, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  getBlob: (path: string, options: Omit<RequestOptions, 'method' | 'body'> = {}) =>
    requestBlob(path, { ...options, method: 'GET' }),
};

let refreshInFlight: Promise<boolean> | null = null;

const shouldSkipRefresh = (path: string): boolean =>
  path === '/auth/refresh' ||
  path === '/auth/login' ||
  path.startsWith('/auth/qr-login');

const request = async <T>(path: string, options: RequestOptions): Promise<T> => {
  const response = await send(path, options);
  if (response.status !== 401 || shouldSkipRefresh(path)) {
    return handle<T>(response);
  }
  const refreshed = await refreshSession();
  if (!refreshed) {
    onSessionLost();
    return handle<T>(response);
  }
  return handle<T>(await send(path, options));
};

const requestBlob = async (path: string, options: RequestOptions): Promise<Blob> => {
  const response = await send(path, options);
  if (response.status === 401 && !shouldSkipRefresh(path)) {
    const refreshed = await refreshSession();
    if (!refreshed) {
      onSessionLost();
      throw new ApiError(response.status);
    }
    return requestBlob(path, options);
  }
  if (!response.ok) {
    const body = await response.json().catch(() => undefined);
    throw new ApiError(response.status, body as ApiErrorBody | undefined);
  }
  return response.blob();
};

const send = async (path: string, options: RequestOptions): Promise<Response> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'X-Trinity-Client': `${CLIENT_ID}/${CLIENT_VERSION}`,
  };
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !isFormData) headers['Content-Type'] = 'application/json';

  const token = getStoredAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (options.churchId) headers['X-Church-Id'] = options.churchId;

  return fetch(`${BASE_URL}${path}${queryString(options.query)}`, {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
    cache: 'no-store',
    body:
      options.body === undefined
        ? undefined
        : isFormData
          ? (options.body as FormData)
          : JSON.stringify(options.body),
    signal: options.signal,
  });
};

const refreshHeaders = (): Record<string, string> => ({
  Accept: 'application/json',
  'X-Trinity-Client': `${CLIENT_ID}/${CLIENT_VERSION}`,
});

const refreshWithStoredToken = async (): Promise<boolean> => {
  const storedRefreshToken = getStoredRefreshToken();
  if (!storedRefreshToken) return false;

  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: {
      ...refreshHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken: storedRefreshToken }),
  });

  if (!response.ok) return false;

  const data = (await response.json().catch(() => null)) as {
    accessToken?: string;
    refreshToken?: string;
  } | null;

  if (!data?.accessToken || !data.refreshToken) return false;

  saveTokens(data.accessToken, data.refreshToken);
  return true;
};

const refreshWithCookies = async (): Promise<boolean> => {
  const response = await fetch(`${BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: refreshHeaders(),
  });
  return response.ok;
};

const REFRESH_LOCK = 'trinity-auth-refresh';

const runRefresh = async (): Promise<boolean> => {
  if (await refreshWithStoredToken()) return true;
  return refreshWithCookies();
};

const refreshSession = (): Promise<boolean> => {
  refreshInFlight ??= (async () => {
    const locks = globalThis.navigator?.locks;
    if (typeof locks?.request === 'function') {
      return locks.request(REFRESH_LOCK, runRefresh);
    }
    return runRefresh();
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

export const bootstrapSession = async (): Promise<void> => {
  try {
    await refreshSession();
  } catch {
    // No valid persisted session; caller may still try /auth/me.
  }
};

/** Renews the access token before it expires (server default: 15 minutes). */
export const keepSessionAlive = async (): Promise<void> => {
  await refreshSession();
};

export type ConsoleSessionPolicy = {
  rememberMeEnabled: boolean;
  idleTimeoutMinutes: number;
};

export const fetchConsoleSessionPolicy = () => api.get<ConsoleSessionPolicy>('/auth/console-session');

export const recordSessionActivity = async (): Promise<void> => {
  await api.post('/auth/session/activity', undefined);
};

const handle = async <T>(response: Response): Promise<T> => {
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => undefined);
  if (!response.ok) throw new ApiError(response.status, body as ApiErrorBody | undefined);
  return body as T;
};

const queryString = (query: RequestOptions['query']): string => {
  if (!query) return '';
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const serialised = params.toString();
  return serialised ? `?${serialised}` : '';
};

export interface OffsetPage {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}
